import React, { useState, useEffect, useMemo } from "react";
import {
  Check,
  Banknote,
  Calendar as CalendarIcon,
  AlertCircle,
  ArrowUp,
  ArrowDown,
  Search,
  Filter,
} from "lucide-react";
import { loansService } from "../services/api";
import {
  toPersianNumbers,
  formatCurrency,
  toEnglishNumbers,
} from "../utils/numbers";
import { Modal } from "../components/Modal";

interface Loan {
  id: number;
  user_id: number;
  amount: string | number;
  requested_date: string;
  description: string;
  status:
    | "pending"
    | "approved"
    | "approved_registration"
    | "fee_paid"
    | "rejected"
    | "paid";
  first_name: string;
  last_name: string;
  national_code: string;
  mobile: string;
  loan_type_name?: string;
  percentage?: number;
  installments_count?: number;
}

// Robust helper to compare Jalali/Gregorian dates in YYYY/MM/DD or YYYY-MM-DD formats
const compareRequestedDates = (
  dateA?: string,
  dateB?: string,
  direction: "asc" | "desc" = "asc",
) => {
  if (!dateA && !dateB) return 0;
  if (!dateA) return 1;
  if (!dateB) return -1;

  const parseParts = (d: string) => {
    const parts = d.split(/[\/\-]/).map((p) => parseInt(p, 10));
    if (parts.length === 3 && parts.every((n) => !isNaN(n))) {
      return parts[0] * 10000 + parts[1] * 100 + parts[2];
    }
    return null;
  };

  const numA = parseParts(dateA);
  const numB = parseParts(dateB);

  let diff = 0;
  if (numA !== null && numB !== null) {
    diff = numA - numB;
  } else {
    const timeA = new Date(dateA).getTime();
    const timeB = new Date(dateB).getTime();
    if (!isNaN(timeA) && !isNaN(timeB)) {
      diff = timeA - timeB;
    } else {
      diff = String(dateA).localeCompare(String(dateB));
    }
  }

  return direction === "asc" ? diff : -diff;
};

export default function LoanPayments() {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isAdmin = user?.role === "admin";

  const [loans, setLoans] = useState<Loan[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedLoan, setSelectedLoan] = useState<Loan | null>(null);
  const [isPayModalOpen, setIsPayModalOpen] = useState(false);
  const [isPaying, setIsPaying] = useState(false);

  // Sort and filter states
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [searchQuery, setSearchQuery] = useState("");

  // Modal State
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: "success" | "error" | "warning" | "info";
  }>({
    isOpen: false,
    title: "",
    message: "",
    type: "info",
  });

  useEffect(() => {
    fetchLoans();
  }, [sortOrder]);

  const fetchLoans = async () => {
    setLoading(true);
    try {
      const response = await loansService.getLoans({
        sort: "requested_date",
        order: sortOrder,
        status: "fee_paid",
      });
      const loansArray = Array.isArray(response)
        ? response
        : response.data || [];
      // Filter for approved loans ready for payment (status: fee_paid)
      const feePaid = loansArray.filter(
        (loan: Loan) => loan.status === "fee_paid",
      );

      // Ensure client-side sorting by requested_date
      feePaid.sort((a: Loan, b: Loan) => {
        const dateDiff = compareRequestedDates(
          a.requested_date,
          b.requested_date,
          sortOrder,
        );
        if (dateDiff !== 0) return dateDiff;
        return a.id - b.id;
      });

      setLoans(feePaid);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching loans:", error);
      setLoans([]);
      setLoading(false);
    }
  };

  const toggleSortOrder = () => {
    const nextOrder = sortOrder === "asc" ? "desc" : "asc";
    setSortOrder(nextOrder);
  };

  const filteredLoans = useMemo(() => {
    if (!searchQuery.trim()) return loans;
    const query = toEnglishNumbers(searchQuery.trim().toLowerCase());
    return loans.filter((loan) => {
      const fullName =
        `${loan.first_name || ""} ${loan.last_name || ""}`.toLowerCase();
      const nationalCode = (loan.national_code || "").toLowerCase();
      const mobile = (loan.mobile || "").toLowerCase();
      const reqDate = (loan.requested_date || "").toLowerCase();
      const typeName = (loan.loan_type_name || "").toLowerCase();
      return (
        fullName.includes(query) ||
        nationalCode.includes(query) ||
        mobile.includes(query) ||
        reqDate.includes(query) ||
        typeName.includes(query)
      );
    });
  }, [loans, searchQuery]);

  const handlePay = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedLoan) return;

    setIsPaying(true);
    try {
      const result = await loansService.payLoan(selectedLoan.id);

      setModalState({
        isOpen: true,
        title: "پرداخت موفق",
        message:
          result.message || "وام با موفقیت پرداخت شد و اقساط ایجاد گردید.",
        type: "success",
      });

      setIsPayModalOpen(false);
      setSelectedLoan(null);
      fetchLoans();
    } catch (error: any) {
      console.error("Error paying loan:", error);
      setModalState({
        isOpen: true,
        title: "خطا",
        message: error.response?.data?.message || "خطا در پرداخت وام.",
        type: "error",
      });
    } finally {
      setIsPaying(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            پرداخت وام‌ها
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            لیست وام‌های تایید شده و آماده پرداخت (مرتب شده بر اساس تاریخ
            درخواست)
          </p>
        </div>

        {/* Quick controls for sort & order */}
        <div className="flex flex-wrap items-center gap-3">
          {!isAdmin && (
            <span className="px-3.5 py-2 bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800 rounded-xl text-xs font-semibold">
              حالت فقط مشاهده (سرمایه‌گذار)
            </span>
          )}

          <button
            type="button"
            onClick={toggleSortOrder}
            className="flex items-center gap-2 px-3.5 py-2 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700/80 border border-slate-200 dark:border-slate-700 rounded-xl text-xs sm:text-sm font-medium text-slate-700 dark:text-slate-200 shadow-sm transition-all"
            title="تغییر ترتیب صعودی / نزولی تاریخ درخواست"
          >
            <Filter className="w-4 h-4 text-slate-400" />
            <span>ترتیب تاریخ:</span>
            {sortOrder === "asc" ? (
              <span className="flex items-center gap-1 font-semibold text-persian-600 dark:text-persian-400">
                <ArrowUp className="w-3.5 h-3.5" />
                قدیمی به جدید (نوبت اول)
              </span>
            ) : (
              <span className="flex items-center gap-1 font-semibold text-blue-600 dark:text-blue-400">
                <ArrowDown className="w-3.5 h-3.5" />
                جدید به قدیم
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 flex flex-col sm:flex-row items-center justify-between gap-3">
        <div className="relative w-full sm:w-80">
          <Search className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="جستجو متقاضی، کد ملی، شماره موبایل..."
            className="w-full h-10 pr-9 pl-3 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 rounded-xl text-xs sm:text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:border-persian-500 focus:ring-2 focus:ring-persian-50 dark:focus:ring-persian-500/20 transition-all"
          />
        </div>

        <div className="text-xs text-slate-500 dark:text-slate-400 self-end sm:self-center">
          تعداد وام‌های آماده پرداخت:{" "}
          <strong className="text-slate-900 dark:text-white font-bold text-sm">
            {toPersianNumbers(filteredLoans.length)}
          </strong>{" "}
          فقره
        </div>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-right border-collapse whitespace-nowrap">
            <thead>
              <tr className="bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700">
                <th className="p-4 text-sm font-semibold text-slate-700 dark:text-slate-300 w-16 text-center">
                  نوبت
                </th>
                <th className="p-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
                  متقاضی
                </th>
                <th className="p-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
                  کد ملی
                </th>
                <th className="p-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
                  نوع وام
                </th>
                <th className="p-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
                  مبلغ / کارمزد
                </th>
                <th
                  onClick={toggleSortOrder}
                  className="p-4 text-sm font-semibold text-slate-700 dark:text-slate-300 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors select-none group"
                  title="برای تغییر ترتیب مرتب‌سازی کلیک کنید"
                >
                  <div className="flex items-center gap-1.5">
                    <CalendarIcon className="w-4 h-4 text-slate-400 group-hover:text-persian-600 transition-colors" />
                    <span>تاریخ درخواست</span>
                    <span className="inline-flex items-center justify-center p-1 rounded bg-slate-200/60 dark:bg-slate-700 text-persian-600 dark:text-persian-400">
                      {sortOrder === "asc" ? (
                        <ArrowUp className="w-3.5 h-3.5" />
                      ) : (
                        <ArrowDown className="w-3.5 h-3.5" />
                      )}
                    </span>
                  </div>
                </th>
                <th className="p-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
                  عملیات
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
              {loading ? (
                <tr>
                  <td
                    colSpan={7}
                    className="p-8 text-center text-slate-500 dark:text-slate-400"
                  >
                    <div className="flex flex-col items-center justify-center space-y-4">
                      <div className="w-8 h-8 border-4 border-persian-600 border-t-transparent rounded-full animate-spin"></div>
                      <p>در حال دریافت اطلاعات...</p>
                    </div>
                  </td>
                </tr>
              ) : filteredLoans.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="p-8 text-center text-slate-500 dark:text-slate-400"
                  >
                    <div className="flex flex-col items-center justify-center space-y-3">
                      <AlertCircle className="w-12 h-12 text-slate-300 dark:text-slate-600" />
                      <p>
                        {searchQuery
                          ? "هیچ وامی مطابق با جستجوی شما یافت نشد."
                          : "هیچ وام آماده پرداختی وجود ندارد."}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredLoans.map((loan, index) => (
                  <tr
                    key={loan.id}
                    className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                  >
                    <td className="p-4 text-center font-bold text-sm text-slate-500 dark:text-slate-400">
                      <span className="inline-block w-7 h-7 leading-7 text-center rounded-lg bg-slate-100 dark:bg-slate-700/60 font-semibold text-slate-700 dark:text-slate-200">
                        {toPersianNumbers(index + 1)}
                      </span>
                    </td>
                    <td className="p-4 font-medium text-slate-900 dark:text-white">
                      {loan.first_name} {loan.last_name}
                    </td>
                    <td className="p-4 text-slate-600 dark:text-slate-300">
                      {toPersianNumbers(loan.national_code)}
                    </td>
                    <td className="p-4 text-slate-600 dark:text-slate-300">
                      {loan.loan_type_name || "-"}
                    </td>
                    <td className="p-4">
                      <div className="flex flex-col gap-1 text-sm text-slate-700 dark:text-slate-300 font-medium">
                        <div className="flex items-center gap-1">
                          <Banknote className="w-4 h-4 text-slate-400" />
                          {formatCurrency(loan.amount)}
                        </div>
                        <div className="text-xs text-slate-500 dark:text-slate-400">
                          {toPersianNumbers(loan.percentage || 0)}٪ کارمزد /{" "}
                          {toPersianNumbers(loan.installments_count || 0)} قسط
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2 font-medium text-slate-800 dark:text-slate-200">
                        <CalendarIcon className="w-4 h-4 text-persian-500" />
                        <span className="font-bold bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded border border-slate-200/60 dark:border-slate-700 text-xs sm:text-sm">
                          {toPersianNumbers(loan.requested_date)}
                        </span>
                      </div>
                    </td>
                    <td className="p-4">
                      {isAdmin ? (
                        <button
                          onClick={() => {
                            setSelectedLoan(loan);
                            setIsPayModalOpen(true);
                          }}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/40 rounded-lg text-sm font-medium transition-colors"
                        >
                          <Check className="w-4 h-4" />
                          <span>پرداخت وام</span>
                        </button>
                      ) : (
                        <span className="text-xs text-slate-400 font-medium px-2 py-1">
                          در نوبت پرداخت
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {isPayModalOpen && selectedLoan && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-700 w-full max-w-lg">
            <div className="p-6 border-b border-slate-200 dark:border-slate-700">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                تایید پرداخت وام
              </h2>
            </div>

            <div className="p-6 bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700">
              <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                <div>
                  <span className="text-slate-500 dark:text-slate-400 block mb-1">
                    نام متقاضی
                  </span>
                  <span className="font-medium text-slate-900 dark:text-white">
                    {selectedLoan.first_name} {selectedLoan.last_name}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500 dark:text-slate-400 block mb-1">
                    کد ملی
                  </span>
                  <span className="font-medium text-slate-900 dark:text-white">
                    {toPersianNumbers(selectedLoan.national_code)}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500 dark:text-slate-400 block mb-1">
                    مبلغ وام
                  </span>
                  <span className="font-medium text-persian-600 dark:text-persian-400">
                    {formatCurrency(selectedLoan.amount)}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500 dark:text-slate-400 block mb-1">
                    تاریخ درخواست
                  </span>
                  <span className="font-medium text-slate-900 dark:text-white">
                    {toPersianNumbers(selectedLoan.requested_date)}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500 dark:text-slate-400 block mb-1">
                    نوع وام
                  </span>
                  <span className="font-medium text-slate-900 dark:text-white">
                    {selectedLoan.loan_type_name || "-"}
                  </span>
                </div>
              </div>

              <div className="bg-blue-50 dark:bg-slate-700/50 p-4 rounded-xl border border-blue-100 dark:border-slate-600">
                <p className="text-sm text-blue-800 dark:text-slate-200 leading-relaxed">
                  با تایید پرداخت، کد وام و اقساط ماهانه بر اساس{" "}
                  <strong>
                    {toPersianNumbers(selectedLoan.installments_count || 0)}
                  </strong>{" "}
                  ماه و کارمزد{" "}
                  <strong>
                    {toPersianNumbers(selectedLoan.percentage || 0)}٪
                  </strong>{" "}
                  ایجاد خواهد شد.
                </p>
              </div>
            </div>

            <form onSubmit={handlePay} className="p-6">
              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={isPaying}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white h-10 rounded-xl font-medium transition-colors disabled:opacity-50"
                >
                  {isPaying ? "در حال پرداخت..." : "تایید و پرداخت"}
                </button>
                <button
                  type="button"
                  onClick={() => setIsPayModalOpen(false)}
                  disabled={isPaying}
                  className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 dark:bg-slate-700 dark:hover:bg-slate-600 dark:text-slate-200 h-10 rounded-xl font-medium transition-colors"
                >
                  انصراف
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Modal
        isOpen={modalState.isOpen}
        onClose={() => setModalState({ ...modalState, isOpen: false })}
        title={modalState.title}
        message={modalState.message}
        type={modalState.type}
      />
    </div>
  );
}
